// eslint-disable-next-line no-unused-vars
import React from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { array, bool, func, object, string } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  SkeletonText,
  SkeletonPlaceholder,
  Button,
  Loading
} from '@carbon/react';
import { Download, ChevronRight } from 'lucide-react';
import { Carousel } from 'react-responsive-carousel';

import IFrameComponent from 'Components/IFrame/IFrame';
import { CURRENCY_EUR, PERMISSION } from 'Constants';
import { Can } from 'Components/shared';
import useNumberUtils from 'Hooks/useNumberUtils';
import Img from 'Containers/Image/Image';
import Categories from 'Containers/Menus/Categories';
import BadgesSection from 'Components/Dashboard/BadgesSection';
import Promotions from 'Components/Dashboard/Promotions';
import Services from 'Components/Dashboard/Services';
import Notifications from 'Components/Dashboard/Notifications';

import BannerApplePortalDesktop from 'Assets/images/BannerAppleWebshop1830x207.jpg';
import BannerApplePortalTablet from 'Assets/images/BannerAppleWebshop1024x480px.jpg';
import BannerDefault from 'Assets/images/banner-default-linkedin.jpg';

const StyledDashboardComponent = styled.div`
  h2 {
    color: #3a3a3a;
    font-size: 24px;
    font-weight: 800;
    line-height: 28px;
    margin: 10px 0 15px;
    text-transform: uppercase;
  }

  .dashboardSummary {
    margin-top: 20px;
    position: relative;

    &:before {
      content: "";
      position: absolute;
      top: -20px;
      bottom: -20px;
      left: -100%;
      right: -100%;
      background-color: #F3F3F3;
      z-index: -1;
    }
  }

  .accountStatus {
    display: flex;
    gap: 32px;
    align-items: center;
    justify-content: start;

    h4 {
      font-size: 20px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .creditLeftLabels {
      font-size: 14px;
      color: #717171;
    }

    .creditLeft {
      width: 100%;
      max-width: 600px;
      font-size: 14px;
      color: #717171;

      .creditLeftLabels {
        display: flex;
        justify-content: space-between;

        &.creditLimit {
          justify-content: end;
        }

        > div > span {
          text-transform: capitalize;
          margin: 0 5px;

          &.creditLeftLabel {
            color: #F47926;
          }

          &.consumedCreditLabel {
            color: #498246;
          }
        }
      }
    }
  }

  .paymentDue {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-around;
    gap: 8px;
    font-size: 20px;
    line-height: 40px;

    > div {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;

      span {
        text-transform: capitalize;
        font-size: 24px;
      }

      .balanceValue {
        color: #e8720b;
      }
    }
  }

  .productsLink {
    display: none;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 40px;
    border-radius: 2px;
    color: #fff;
    margin-bottom: 40px;
    background: #F47926;
    color: #fff;

    svg {
      margin-left: 8px;
    }
  }

  .applePortal {
    position: relative;
    padding-bottom: 20px;

    button {
      border: none;
      padding: 0;
      background: none;
      cursor: pointer;
      display: block;

      img {
        width: 100%;
        height: auto;
        display: block;
      }
    }

    .cds--loading {
      position: absolute;
      left: calc(50% - 0.5rem);
      top: calc(50% - 0.5rem);
      width: 1rem;
      height: 1rem;
    }
  }

  .heroSection {
    display: grid;
    grid-template-columns: 260px minmax(0, 1fr);
    grid-column-gap: 30px;

    .categoriesSection {
      position: relative;
      z-index: 2;
      background-color: ${ props => props.theme.colors.shadowGray };

      .menuContainer {
        top: -10px;
      }

      .categoriesAllProducts {
        font-weight: 600;
        padding: 16px 0 16px 55px;
        color: #6A6767;
      }
    }

    .sideSliders {
      position: relative;
      z-index: 1;
      min-width: 0;
      overflow: hidden;
    }
  }

  .sliderSection {
    display: flex;
    gap: 32px;

    > div, section {
      flex: 1;
    }
  }

  @media screen and (max-width: 1440px) {
    .creditLeft {
      width: 100%;
    }
  }

  @media screen and (max-width: 1024px) {
    .categoriesSection,
    .accountStatus {
      display: none;
    }

    .heroSection {
      grid-template-columns: 1fr;
    }
  }

  @media screen and (max-width: 960px) {
    .productsLink {
      display: flex;
    }

    .banners {
      flex-direction: column;
    }
  }

  @media screen and (max-width: 767px) {
    .creditLeft
    .creditLeftLabels {
      flex-direction: column;
      gap: 12px;
    }
  }
`;

const StyledButton = styled(Button)`
  margin-top: 32px;
  max-width: 100%;
  width: 100%;
  height: 40px;
  padding: 0;
  justify-content: center;
  border-radius: 10px;

  svg {
    margin-right: 8px;
  }

  &.primary {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    background: #F47926;
    color: #fff;
    border-radius: 2px;
    padding: 8px 32px;

    svg {
      stroke: none;
    }
  }
`;

const StyledCarouselContainer = styled.section`
  .slide a {
    display: block;
  }
`;

const StyledCarousel = styled(Carousel)`
`;

const StyledIndicator = styled.div`
  overflow: hidden;
  position: relative;
  height: 10px;
  border-radius: 5px;
  margin: 10px 0;
  background-color: #FFF;

  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    height: 10px;
    width: 100%;
    max-width: 0;
    ${ props => `max-width: ${ props.percentage() }%` };
    transition: max-width 0.35s ease;
    background-color: ${ props => props.theme.products.colors.isResealed };
    border-radius: 4px;
  }
`;

const StyledNotificationModal = styled(ComposedModal)`
  .title {
    font-size: 24px;
    line-height: 26px;
  }

  .date {
    font-size: 14px;
    line-height: 20px;
    color: #5F5F60;
  }

  .content {
    padding-right: 0;
    margin: 24px 0;
    font-size: 16px;
    line-height: 21px;
    white-space: pre-line;
  }
`;

const StyledSliderPlaceholder = styled(SkeletonPlaceholder)`
  width: 100%;
  aspect-ratio: 3;
  height: auto;
`;

const DashboardComponent = ({
  credit,
  creditLoading,
  isExternalReseller,
  defaultCurrency,
  notifications,
  dashboardLoading,
  badges,
  handleSMIS,
  loadingSMIS,
  handleHPE,
  loadingHPE,
  handleApple,
  loadingApple,
  IQuote,
  handleIQuoteBackButton,
  handleFileDownload,
  handleDownload,
  selectedNotification,
  modalOpen,
  onModalClose,
  handleReadMore,
  slides,
  brandSlides,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatNumber } = useNumberUtils();
  const { promotions, services } = brandSlides || {};

  let creditRemaining;
  let creditTotal;

  if (isExternalReseller) {
    if (defaultCurrency === CURRENCY_EUR) {
      creditRemaining = credit?.remainingEur;
      creditTotal = credit?.totalEur;
    } else {
      creditRemaining = credit?.remainingUsd;
      creditTotal = credit?.totalUsd;
    }
  } else {
    creditRemaining = credit?.remaining;
    creditTotal = credit?.total;
  }

  return (
    <StyledDashboardComponent theme={ theme }>
      { IQuote && <IFrameComponent source={ IQuote } onBackButtonClick={ handleIQuoteBackButton } /> }
      <StyledNotificationModal
        onClose={ onModalClose }
        open={ modalOpen }
        size="md"
        selectorPrimaryFocus=".cds--modal-content"
      >
        <ModalHeader>
          <h4>{ t('pages.notification') }</h4>
        </ModalHeader>
        <ModalBody>
          <h4 className="title">{selectedNotification.title}</h4>
          <span className="date">{selectedNotification.startDate}</span>
          <p className="content">{ selectedNotification.content }</p>
          { selectedNotification.filename && (
            <StyledButton
              kind="ghost"
              className="primary"
              theme={ theme }
              onClick={ () => {
                if (selectedNotification?.messageId === 0) {
                  handleDownload(selectedNotification?.filename);
                } else {
                  handleFileDownload(selectedNotification.messageId);
                }
              } }
            >
              <Download />
              { t('pages.download') }
            </StyledButton>
          ) }
        </ModalBody>
      </StyledNotificationModal>
      <div className="dashboardSummary">
        <Can permission={ PERMISSION.VIEW_ACCOUNT_STATUS }>
          <section className="accountStatus">
            <h4>{ t('pages.accountStatus') }</h4>
            <div className="creditLeft">
              <div className="creditLeftLabels">
                {(!credit || creditLoading)
                  ? <SkeletonText /> : (
                    <>
                      <div>
                        { t('user.consumedCredit') }:
                        <span className="consumedCreditLabel">{ formatNumber((creditTotal || 0) - (creditRemaining || 0), { currency: defaultCurrency }) }</span>
                      </div>
                      <div>
                        { t('user.creditLeft') }:
                        <span className="creditLeftLabel">{ formatNumber(creditRemaining, { currency: defaultCurrency }) }</span>
                      </div>
                    </>
                  )}
              </div>
              <StyledIndicator
                theme={ theme }
                percentage={ () => {
                  if (credit) {
                    const percentage = (creditRemaining / creditTotal) * 100;
                    return percentage < 0 ? 0 : percentage;
                  }
                  return 0;
                } }
              />
            </div>
            <div className="creditLeftLabels creditLimit">
              {(!credit || creditLoading)
                ? <SkeletonText /> : (
                  <div>
                    { t('user.creditLimit') }:
                    <span>{ formatNumber(creditTotal, { currency: defaultCurrency }) }</span>
                  </div>
                )}
            </div>
          </section>
        </Can>
        <BadgesSection badges={ badges } badgesLoading={ dashboardLoading } />
      </div>
      <Link className="productsLink" to="/products">
        { t('pages.seeAllProducts') }
        <ChevronRight />
      </Link>
      <div className="applePortal">
        <button type="button" onClick={ handleApple }>
          <picture>
            <source media="(max-width: 1024px)" srcSet={ BannerApplePortalTablet } />
            <img src={ BannerApplePortalDesktop } alt="Apple Portal" />
          </picture>
        </button>
        { loadingApple && (
          <Loading withOverlay={ false } active small />
        )}
      </div>
      <div className="heroSection">
        <div className="categoriesSection">
          <Link to="/products">
            <p className="categoriesAllProducts">{ t('profile.allProducts') }</p>
          </Link>
          <Categories isStatic />
        </div>
        <div className="sideSliders">
          <div className="heroSlider">
            { dashboardLoading ? (
              <StyledSliderPlaceholder />
            ) : (
              <StyledCarouselContainer
                className="carouselContainer"
                theme={ theme }
              >
                <div className="sliderRoot">
                  <StyledCarousel
                    theme={ theme }
                    emulateTouch
                    swipeable
                    showStatus={ false }
                    showIndicators={ slides.length > 1 }
                    infiniteLoop
                    showArrows={ slides.length > 1 }
                    autoPlay
                    autoPlaySpeed={ 5000 }
                    showThumbs={ false }
                  >
                    { slides.length > 0 ? slides.map(slide => (
                      <div key={ slide.erpPromotionId }>
                        { slide.link ? (
                          <a
                            href={ slide.link }
                            target="_blank"
                          >
                            <Img
                              src={ slide.thumbnail }
                              loader={ <Loading withOverlay={ false } active small /> }
                              alt={ slide.erpPromotionId }
                            />
                          </a>
                        ) : (
                          <Link
                            to={ `/products?promotion=${ slide.erpPromotionId }` }
                          >
                            <Img
                              src={ slide.thumbnail }
                              loader={ <Loading withOverlay={ false } active small /> }
                              alt={ slide.erpPromotionId }
                            />
                          </Link>
                        ) }
                      </div>
                    )) : (
                      <div>
                        <a
                          href="https://www.linkedin.com/showcase/nod-academy/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img src={ BannerDefault } alt="NOD Academy" />
                        </a>
                      </div>
                    ) }
                  </StyledCarousel>
                </div>
              </StyledCarouselContainer>
            )}
          </div>
          <Promotions loading={ dashboardLoading } promotions={ promotions } />
        </div>
      </div>
      <Services
        loading={ dashboardLoading }
        services={ services }
        actionHandlers={ {
          hpe: { onClick: handleHPE, loading: loadingHPE },
          smis: { onClick: handleSMIS, loading: loadingSMIS },
        } }
      />
      <Notifications
        notifications={ notifications }
        notificationsLoading={ dashboardLoading }
        handleReadMore={ handleReadMore }
        handleFileDownload={ handleFileDownload }
        handleDownload={ handleDownload }
      />
    </StyledDashboardComponent>
  );
};

DashboardComponent.propTypes = {
  credit: object,
  creditLoading: bool,
  isExternalReseller: bool,
  defaultCurrency: string,
  notifications: array,
  dashboardLoading: bool,
  badges: object,
  handleSMIS: func,
  loadingSMIS: bool,
  handleHPE: func,
  loadingHPE: bool,
  handleApple: func,
  loadingApple: bool,
  IQuote: string,
  handleIQuoteBackButton: func,
  handleFileDownload: func,
  handleDownload: func,
  selectedNotification: object,
  modalOpen: bool,
  onModalClose: func,
  handleReadMore: func,
  slides: array,
  brandSlides: array,
};

DashboardComponent.displayName = 'DashboardComponent';

export default DashboardComponent;
